import React, { useEffect, useState } from "react";
import Modal from "@mui/material/Modal";
import { useNavigate } from "react-router-dom";
import { Icon } from "react-icons-kit";
import { eyeOff } from "react-icons-kit/feather/eyeOff";
import { eye } from "react-icons-kit/feather/eye";
import { FaEye, FaEyeSlash } from "react-icons/fa";
import { isMobile, isTablet, isDesktop } from "react-device-detect";

import "./StudentLogin.css";
import StudentDashBoad from "./StudentDashBoad";
import MakePayment from "./MakePayment";
import { API_BASE_URL, LOGOUT_URL } from "./config";
import { OFFICE_CONTACT_NUMBER } from "./PaymentConfig";
import { useLoading } from "./LoadingContext";

export default function StudentLogin() {
  const navigate = useNavigate();
  const { showLoading, hideLoading } = useLoading();
  const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

  const [isLoginOpen, setIsLoginOpen] = useState(true);
  const [isForgotOpen, setIsForgotOpen] = useState(false);
  const [isNewUserOpen, setIsNewUserOpen] = useState(false);
  const [isDeviceWarningOpen, setIsDeviceWarningOpen] = useState(false);

  const [mobileNumber, setMobileNumber] = useState("");
  const [password, setPassword] = useState("");
  const [passwordType, setPasswordType] = useState("password");
  const [passwordIcon, setPasswordIcon] = useState(eyeOff);

  const [existingUser, setExistingUser] = useState(false);
  const [incorrectPassword, setIncorrectPassword] = useState("");

  const [openStudentDashboard, setOpenStudentDashboard] = useState(false);

  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [newUserMail, setNewUserMail] = useState("");
  const [newUserSubmitted, setNewUserSubmitted] = useState(false);

  const [fpEmail, setFpEmail] = useState("");
  const [fpEmailError, setFpEmailError] = useState("");
  const [wrongMail, setWrongMail] = useState("");
  const [emailVerified, setEmailVerified] = useState(false);
  const [otpSendSuccess, setOtpSendSuccess] = useState(false);
  const [enteredOtp, setEnteredOtp] = useState("");
  const [otpVerified, setOtpVerified] = useState(false);
  const [otpCheck, setOtpCheck] = useState(true);
  const [extraLetters, setExtraLetters] = useState("");

  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const [paymentDetails, setPaymentDetails] = useState(null);
  const [showMakePayment, setShowMakePayment] = useState(false);

  const logoutUrl = LOGOUT_URL;

  useEffect(() => {
    const authenticateUser = async () => {
      const studentUniqueId = localStorage.getItem("student_unique_id");
      const token = localStorage.getItem("token");

      if (!studentUniqueId || !token) {
        localStorage.removeItem("student_unique_id");
        localStorage.removeItem("token");
        return;
      }

      try {
        const response = await fetch(`${API_BASE_URL}/verify_token`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            token,
          },
          body: JSON.stringify({ student_id: studentUniqueId }),
        });

        if (response.status === 403) {
          alert(
            "⚠️ Your account is not activated yet.\n\n" +
            "📞 Our executive will contact you shortly to discuss future coordination and verify your mobile number.\n\n" +
            "✉️ If you need to update your mobile number, please email us from the registered email ID."
          );

          localStorage.removeItem("student_unique_id");
          localStorage.removeItem("token");
          window.location.href = logoutUrl;
          return;
        }

        const data = await response.json();

        if (response.ok && data === "success") {
          setIsLoginOpen(false);
          setOpenStudentDashboard(true);
          hideNavbar();
        } else {
          localStorage.removeItem("student_unique_id");
          localStorage.removeItem("token");
        }
      } catch (error) {
        console.error("Token verification error:", error);
        localStorage.removeItem("student_unique_id");
        localStorage.removeItem("token");
      }
    };

    authenticateUser();
    // eslint-disable-next-line
  }, []);

  const hideNavbar = () => {
    const upparNav = document.querySelector(".uppar-nav");
    if (upparNav) upparNav.style.display = "none";
  };

  const goHome = () => {
    setIsLoginOpen(false);
    setIsForgotOpen(false);
    setIsNewUserOpen(false);
    setIsDeviceWarningOpen(false);
    navigate("/");
  };

  const togglePassword = () => {
    if (passwordType === "password") {
      setPasswordType("text");
      setPasswordIcon(eye);
    } else {
      setPasswordType("password");
      setPasswordIcon(eyeOff);
    }
  };

  const validateEmail = (email) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

  const generateRandomCode = () => {
    const chars = "ABCDEFGHIJKLMNOPQRSTUVWXYZ";
    let result = "";
    for (let i = 0; i < 4; i++) {
      result += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    return result;
  };

  const saveToLocalStorage = (key, value) => {
    localStorage.setItem(key, value);
    return Promise.resolve(true);
  };

  const handleMobileChange = (e) => {
    const value = e.target.value.replace(/\D/g, "").slice(0, 10);
    setMobileNumber(value);
    setExistingUser(false);
    setPassword("");
    setIncorrectPassword("");
  };

  const handlePasswordChange = (e) => {
    setPassword(e.target.value);
    setIncorrectPassword("");
  };

  const handleFirstNameChange = (e) => {
    const value = e.target.value;
    if (/^[A-Za-z\s]*$/.test(value)) setFirstName(value);
  };

  const handleLastNameChange = (e) => {
    const value = e.target.value;
    if (/^[A-Za-z\s]*$/.test(value)) setLastName(value);
  };

  const saveMobileNumber = async (mobile) => {
    try {
      const response = await fetch(
        `${API_BASE_URL}/probable_student_mobile_save?mobile_number=${mobile}&status=new_prob`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
        }
      );

      const data = await response.json();

      if (response.ok) {
        console.log("Mobile number saved:", data);
        setIsLoginOpen(false);
        setIsNewUserOpen(true);
      } else {
        console.error("Failed to save mobile number:", data.detail);
      }
    } catch (error) {
      console.error("Mobile save error:", error);
    }
  };

  const saveProbableStudentDetails = async () => {
    try {
      const response = await fetch(
        `${API_BASE_URL}/probable_student_all_info_save?first_name=${encodeURIComponent(
          firstName
        )}&last_name=${encodeURIComponent(
          lastName
        )}&email=${encodeURIComponent(
          newUserMail
        )}&mobile_number=${mobileNumber}&status=new_prob`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
        }
      );

      const data = await response.json();

      if (response.ok) {
        console.log("Student details saved:", data);
        setNewUserSubmitted(true);

        setTimeout(() => {
          setIsNewUserOpen(false);
          navigate("/");
        }, 4500);
      } else {
        console.error("Failed to save student details:", data.detail);
      }
    } catch (error) {
      console.error("Student details save error:", error);
    }
  };

  // const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

  const handleMobileSubmit = async (e) => {
    e.preventDefault();

    if (!/^\d{10}$/.test(mobileNumber)) {
      alert("📱 Mobile Number must be exactly 10 digits!");
      return;
    }

    showLoading("📞 Checking your registered mobile number...");

    try {
      const response = await fetch(
        `${API_BASE_URL}/check_mobile_number/${mobileNumber}`
      );
      const data = await response.json();

      if (data.exists) {
        if (isDesktop) {
          setExistingUser(true);
          localStorage.setItem("student_unique_id", data.student_id);
        } else if (isMobile || isTablet) {
          setIsLoginOpen(false);
          setIsDeviceWarningOpen(true);
        } else {
          setExistingUser(true);
          localStorage.setItem("student_unique_id", data.student_id);
        }
      } else {
        await saveMobileNumber(mobileNumber);
      }
    } catch (error) {
      console.error("Mobile check error:", error);
      alert("Unable to check mobile number. Please contact support.");
    } finally {
      // await sleep(250);
      await sleep(700);
      hideLoading();
    }
  };

  const loginStudent = async (e) => {
    e.preventDefault();

    if (!/^\d{10}$/.test(mobileNumber)) {
      alert("📱 Mobile Number must be exactly 10 digits!");
      return;
    }

    if (password.length < 6) {
      alert("🔒 Password must be at least 6 characters long.");
      return;
    }

    showLoading("⏳ Checking credentials securely...");

    try {
      const response = await fetch(
        `${API_BASE_URL}/login?mobile_number=${mobileNumber}&password=${encodeURIComponent(
          password
        )}`,
        {
          method: "POST",
          headers: { Accept: "application/json" },
        }
      );

      if (response.status === 410) {
        alert("Account is de-activated as course duration time has finished.");
        localStorage.removeItem("student_unique_id");
        localStorage.removeItem("token");
        window.location.href = logoutUrl;
        return;
      }

      if (response.status === 409) {
        alert(
          "Sign-in successful, but payment is pending. Please complete the payment to continue."
        );
        localStorage.removeItem("student_unique_id");
        localStorage.removeItem("token");
        window.location.href = logoutUrl;
        return;
      }

      if (response.status === 403) {
        alert(
          "⚠️ Your account is not activated yet.\n\n" +
          "📞 Our executive will contact you shortly to discuss future coordination and verify your mobile number."
        );
        localStorage.removeItem("student_unique_id");
        localStorage.removeItem("token");
        window.location.href = logoutUrl;
        return;
      }

      const responseData = await response.json();

      if (responseData.status_code === 202 && responseData.login_allowed === false) {
        if (responseData.payment_status === "REGISTRATION_PAYMENT_PENDING") {
          alert("💳 Registration fee is pending. Please complete registration payment first.");
        } else if (responseData.payment_status === "PROGRAM_PAYMENT_PENDING") {
          alert("💳 Training fee is pending. Please choose One-Time or Installment payment.");
        } else if (responseData.payment_status === "INSTALLMENT_PAYMENT_INSUFFICIENT") {
          alert(
            `💳 Installment payment is pending.\n\nPaid installments: ${responseData.paid_installment_count}\nRequired installments: ${responseData.required_installment_count}\nPending installments: ${responseData.pending_installment_count}\nInstallments to pay: ${(responseData.installment_numbers_to_pay || []).join(", ")}`
          );
        } else {
          alert("💳 Payment is pending. Please complete payment to continue.");
        }

        setPaymentDetails({
          studentId: responseData.student_unique_id,

          paymentStatus: responseData.payment_status,
          nextPaymentStep: responseData.next_payment_step,

          paymentContext: responseData.payment_context,
          planType: responseData.plan_type,

          askStudentFee: responseData.ask_student_fee || 0,
          gstPercent: responseData.gst_percent || 18,
          gstAmount: responseData.gst_amount || 0,
          totalAmount: responseData.total_amount || 0,

          studentDay: responseData.student_day || 1,

          paidInstallmentCount: responseData.paid_installment_count || 0,
          requiredInstallmentCount: responseData.required_installment_count || 0,
          pendingInstallmentCount: responseData.pending_installment_count || 0,

          installmentNumbersToPay: responseData.installment_numbers_to_pay || [],
          missingInstallmentNumbers: responseData.missing_installment_numbers || [],

          paidInstallments: responseData.paid_installments || [],
          paymentOptions: responseData.payment_options || [],

          couponCode: responseData.coupon_code || null,
        });

        setShowMakePayment(true);
        return;
      }
      if (response.ok && responseData.student_unique_id && responseData.token) {
        await saveToLocalStorage(
          "student_unique_id",
          responseData.student_unique_id
        );
        await saveToLocalStorage("token", responseData.token);
        await saveToLocalStorage("first_name", responseData.first_name || "");

        setIncorrectPassword("");
        setIsLoginOpen(false);
        setOpenStudentDashboard(true);
        hideNavbar();
      } else {
        setIncorrectPassword("Incorrect password");
        setTimeout(() => setIncorrectPassword(""), 4500);
      }
    } catch (error) {
      console.error("Login error:", error);
      setIncorrectPassword("Incorrect password or server error");
      setTimeout(() => setIncorrectPassword(""), 4500);
    } finally {
      await sleep(700);
      hideLoading();
    }
  };

  const openForgotPassword = () => {
    setIsLoginOpen(false);
    setIsForgotOpen(true);
    setEmailVerified(false);
    setOtpSendSuccess(false);
    setOtpVerified(false);
    setEnteredOtp("");
    setFpEmail("");
    setFpEmailError("");
    setWrongMail("");
  };

  const handleFpEmailChange = (e) => {
    const value = e.target.value;
    setFpEmail(value);

    if (!value) {
      setFpEmailError("");
      return;
    }

    if (validateEmail(value)) {
      setFpEmailError("");
    } else {
      setFpEmailError("Please enter a valid email address.");
    }
  };

  const sendOtpToEmail = async (studentUniqueId, email) => {
    const code = generateRandomCode();
    setExtraLetters(code);

    const response = await fetch(
      `${API_BASE_URL}/send_mailOTP/${studentUniqueId}?email=${encodeURIComponent(
        email
      )}&code=${code}`,
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
      }
    );

    const data = await response.json();
    return data.status === "success";
  };

  const handleForgotEmailSubmit = async (e) => {
    e.preventDefault();

    if (!validateEmail(fpEmail)) {
      setFpEmailError("Please enter a valid email address.");
      return;
    }

    const studentUniqueId = localStorage.getItem("student_unique_id");

    if (!studentUniqueId) {
      alert("Student ID not found. Please enter mobile number again.");
      setIsForgotOpen(false);
      setIsLoginOpen(true);
      return;
    }

    showLoading("🛠️ Verifying your registered email...");

    try {
      const response = await fetch(
        `${API_BASE_URL}/verify_mail/${studentUniqueId}?email=${encodeURIComponent(
          fpEmail
        )}`
      );

      const data = await response.json();

      if (data.status === "success") {
        setWrongMail("");
        setEmailVerified(true);

        const otpSent = await sendOtpToEmail(studentUniqueId, fpEmail);
        setOtpSendSuccess(otpSent);
      } else {
        setWrongMail("Wrong Email ID");
        setEmailVerified(false);
        setTimeout(() => setWrongMail(""), 3000);
      }
    } catch (error) {
      console.error("Email verify error:", error);
    } finally {
      await sleep(700);
      hideLoading();
    }
  };

  const resendOtp = async (e) => {
    e.preventDefault();

    const studentUniqueId = localStorage.getItem("student_unique_id");

    if (!studentUniqueId) return;

    try {
      setOtpSendSuccess(false);
      const otpSent = await sendOtpToEmail(studentUniqueId, fpEmail);
      setOtpSendSuccess(otpSent);
      setOtpCheck(true);
    } catch (error) {
      console.error("Resend OTP error:", error);
    }
  };

  const verifyOtp = async (e) => {
    e.preventDefault();

    if (!/^\d{6}$/.test(enteredOtp)) {
      setOtpCheck(false);
      setTimeout(() => setOtpCheck(true), 3000);
      return;
    }

    const studentUniqueId = localStorage.getItem("student_unique_id");

    if (!studentUniqueId) return;

    showLoading("📡 Verifying OTP...");

    try {
      const response = await fetch(
        `${API_BASE_URL}/verify_otp/${studentUniqueId}?email=${encodeURIComponent(
          fpEmail
        )}&otp=${enteredOtp}`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
        }
      );

      const data = await response.json();

      if (data.status === "success") {
        setOtpVerified(true);
        setOtpCheck(true);
      } else {
        setOtpVerified(false);
        setOtpCheck(false);
        setTimeout(() => setOtpCheck(true), 3000);
      }
    } catch (error) {
      console.error("OTP verify error:", error);
    } finally {
      await sleep(700);
      hideLoading();
    }
  };

  const handleSubmitNewPassword = async (e) => {
    e.preventDefault();

    if (newPassword.length < 6) {
      alert("🔒 Password must be at least 6 characters long.");
      return;
    }

    if (newPassword !== confirmPassword) {
      alert("🚫 Passwords do not match.");
      return;
    }

    const studentUniqueId = localStorage.getItem("student_unique_id");

    if (!studentUniqueId) {
      alert("Student ID not found. Please login again.");
      return;
    }

    showLoading("🔒 Updating your password securely...");

    try {
      const response = await fetch(`${API_BASE_URL}/change-password`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          email: fpEmail,
          password: newPassword,
          confirm_password: confirmPassword,
          student_unique_id: studentUniqueId,
        }),
      });

      const data = await response.json();

      if (response.ok) {
        alert("🔑 Password changed successfully. Kindly login again.");
        setIsForgotOpen(false);
        setIsLoginOpen(true);
        setPassword("");
        setNewPassword("");
        setConfirmPassword("");
      } else {
        alert(`Failed to change password. Contact support. ${data.detail || ""}`);
      }
    } catch (error) {
      console.error("Password update error:", error);
      alert("An error occurred while changing password.");
    } finally {
      await sleep(700);
      hideLoading();
    }
  };

  const handleNewUserSubmit = async (e) => {
    e.preventDefault();

    if (firstName.trim().length < 2 || lastName.trim().length < 2) {
      alert("Please enter valid first name and last name.");
      return;
    }

    if (!validateEmail(newUserMail)) {
      alert("Please enter a valid email address.");
      return;
    }

    await saveProbableStudentDetails();
  };

  if (openStudentDashboard) {
    return <StudentDashBoad />;
  }

  return (
    <main className="student-login-page">
      {showMakePayment && (
        <MakePayment
          paymentDetails={paymentDetails}
          onPaymentSuccess={() => {
            setShowMakePayment(false);
            setPaymentDetails(null);
            setPassword("");
            setExistingUser(false);
            setIsLoginOpen(true);
          }}
        />
      )}

      {(isLoginOpen || isForgotOpen || isNewUserOpen || isDeviceWarningOpen) &&
        !showMakePayment && <div className="modal-background" />}






      <Modal open={isLoginOpen && !showMakePayment} className="student-modal-outer">
        <div className="student-login-shell">
          <button className="student-close-btn" onClick={goHome}>
            Close
          </button>

          <div className="student-login-left">
            <div className="student-login-brand">
              <img src="/sneh-logo.png" alt="StudyAIPlacement AI" />
              <span>Student Portal</span>
            </div>

            <div className="student-login-heading student-login-heading-left">
              <p>FOR ENROLLED STUDENTS</p>

              <h2>Continue your learning, projects and placement-led journey.</h2>

              <span>
                Login after your admission and fee process is activated. Access your
                roadmap, assignments, progress, fee details, placement-led preparation
                and student dashboard.
              </span>
            </div>

            <div className="student-portal-points">
              <div>
                <strong>Learning Roadmap</strong>
                <span>Track your AI, Data Science and Full Stack journey.</span>
              </div>

              <div>
                <strong>Projects & Assignments</strong>
                <span>Submit work and follow your daily learning discipline.</span>
              </div>

              <div>
                <strong>Placement-Led Preparation</strong>
                <span>Resume, mock interviews, job readiness and career direction.</span>
              </div>
            </div>

            <div className="student-login-note">
              <strong>Not enrolled yet?</strong>
              <span>
                Complete admission first to activate your student account and fee
                process.
              </span>
            </div>
          </div>

          <div className="student-login-right">
            <div className="student-form-card">
              <div className="student-form-title">
                <span>LOGIN</span>
                <h3>Access Student Dashboard</h3>
                <p>Use the mobile number submitted during admission.</p>
              </div>

              <form onSubmit={existingUser ? loginStudent : handleMobileSubmit}>
                <label>Registered Mobile Number</label>

                <input
                  type="text"
                  value={mobileNumber}
                  onChange={handleMobileChange}
                  placeholder="Enter 10-digit mobile number"
                  maxLength={10}
                  inputMode="numeric"
                />

                {mobileNumber.length === 10 && existingUser && (
                  <>
                    <label>Password</label>

                    <div className="student-password-row">
                      <input
                        type={passwordType}
                        value={password}
                        onChange={handlePasswordChange}
                        placeholder="Enter student portal password"
                        maxLength={50}
                      />

                      <button
                        type="button"
                        className="student-eye-btn"
                        onClick={togglePassword}
                      >
                        <Icon icon={passwordIcon} size={22} />
                      </button>
                    </div>

                    {incorrectPassword && (
                      <div className="student-error-box">{incorrectPassword}</div>
                    )}
                  </>
                )}

                <button className="student-primary-btn" type="submit">
                  {existingUser ? "Login to Student Portal" : "Check Student Account"}
                </button>

                {existingUser && (
                  <button
                    type="button"
                    className="student-link-btn"
                    onClick={openForgotPassword}
                  >
                    Forgot Password?
                  </button>
                )}
              </form>

              <div className="student-login-footer">
                <span>Admission pending? Continue from the Admission page.</span>
              </div>
            </div>
          </div>
        </div>
      </Modal>



      <Modal open={isForgotOpen} className="student-modal-outer">
        <div className="student-modal-card">
          <button className="student-close-btn" onClick={goHome}>
            Close
          </button>

          {!emailVerified && (
            <>
              <div className="student-login-heading">
                <p>SECURE RESET</p>
                <h2>Verify your email.</h2>
                <span>
                  Enter your registered email ID. We will verify it and send OTP
                  for password reset.
                </span>
              </div>

              <form onSubmit={handleForgotEmailSubmit}>
                <label>Registered Email Address</label>
                <input
                  type="email"
                  value={fpEmail}
                  onChange={handleFpEmailChange}
                  placeholder="Enter registered email"
                  maxLength={50}
                />

                {fpEmailError && (
                  <div className="student-error-box">{fpEmailError}</div>
                )}

                {wrongMail && <div className="student-error-box">{wrongMail}</div>}

                <button className="student-primary-btn" type="submit">
                  Check Email
                </button>
              </form>
            </>
          )}

          {emailVerified && otpSendSuccess && !otpVerified && (
            <>
              <div className="student-login-heading">
                <p>OTP VERIFICATION</p>
                <h2>Enter secure OTP.</h2>
                <span>
                  OTP has been sent to <strong>{fpEmail}</strong>
                </span>
              </div>

              <form>
                <div className="student-otp-box">
                  <div className="student-extra-code">{extraLetters}</div>
                  <input
                    type="text"
                    maxLength={6}
                    value={enteredOtp}
                    onChange={(e) =>
                      setEnteredOtp(e.target.value.replace(/\D/g, "").slice(0, 6))
                    }
                    placeholder="OTP"
                    inputMode="numeric"
                  />
                </div>

                {!otpCheck && (
                  <div className="student-error-box">Invalid OTP</div>
                )}

                <div className="student-two-btns">
                  <button className="student-primary-btn" onClick={verifyOtp}>
                    Verify OTP
                  </button>

                  <button className="student-secondary-btn" onClick={resendOtp}>
                    Resend OTP
                  </button>
                </div>
              </form>
            </>
          )}

          {emailVerified && !otpSendSuccess && !otpVerified && (
            <div className="student-info-box">
              OTP request is being processed. Please check your inbox.
            </div>
          )}

          {emailVerified && otpSendSuccess && otpVerified && (
            <>
              <div className="student-login-heading">
                <p>PASSWORD RESET</p>
                <h2>Create new password.</h2>
                <span>
                  OTP verified successfully. Set your new student portal
                  password.
                </span>
              </div>

              <form onSubmit={handleSubmitNewPassword}>
                <label>New Password</label>
                <div className="student-password-row">
                  <input
                    type={showNewPassword ? "text" : "password"}
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    placeholder="Enter new password"
                    maxLength={50}
                  />

                  <button
                    type="button"
                    className="student-eye-btn"
                    onClick={() => setShowNewPassword(!showNewPassword)}
                  >
                    {showNewPassword ? <FaEyeSlash /> : <FaEye />}
                  </button>
                </div>

                <label>Confirm New Password</label>
                <div className="student-password-row">
                  <input
                    type={showConfirmPassword ? "text" : "password"}
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    placeholder="Confirm new password"
                    maxLength={50}
                  />

                  <button
                    type="button"
                    className="student-eye-btn"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  >
                    {showConfirmPassword ? <FaEyeSlash /> : <FaEye />}
                  </button>
                </div>

                <button className="student-primary-btn" type="submit">
                  Submit New Password
                </button>
              </form>
            </>
          )}
        </div>
      </Modal>

      <Modal open={isNewUserOpen} className="student-modal-outer">
        <div className="student-modal-card student-wide-card">
          {!newUserSubmitted ? (
            <>

              <button className="student-close-btn" onClick={goHome} aria-label="Close login">
                <span>Close</span>
                <b>×</b>
              </button>

              <div className="student-login-heading">
                <p>NEW STUDENT ENQUIRY</p>
                <h2>Welcome, new learner.</h2>
                <span>
                  This mobile number is not registered yet. Fill your details
                  and our team will contact you soon. You can also call{" "}
                  <a href={`tel:${OFFICE_CONTACT_NUMBER}`}>
                    {OFFICE_CONTACT_NUMBER}
                  </a>
                </span>
              </div>

              <form onSubmit={handleNewUserSubmit}>
                <div className="student-form-grid">
                  <div>
                    <label>First Name</label>
                    <input
                      type="text"
                      value={firstName}
                      onChange={handleFirstNameChange}
                      placeholder="Enter first name"
                      maxLength={50}
                    />
                  </div>

                  <div>
                    <label>Last Name</label>
                    <input
                      type="text"
                      value={lastName}
                      onChange={handleLastNameChange}
                      placeholder="Enter last name"
                      maxLength={50}
                    />
                  </div>
                </div>

                <label>Mobile Number</label>
                <div className="student-readonly-box">{mobileNumber}</div>

                <label>Email Address</label>
                <input
                  type="email"
                  value={newUserMail}
                  onChange={(e) => setNewUserMail(e.target.value)}
                  placeholder="Enter email address"
                  maxLength={50}
                />

                <button className="student-primary-btn" type="submit">
                  Submit Enquiry
                </button>
              </form>
            </>
          ) : (
            <div className="student-success-box">
              <span>🎓</span>
              <h2>Request received!</h2>
              <p>
                Welcome to the future of learning. Our academic team will reach
                out soon.
              </p>
            </div>
          )}
        </div>
      </Modal>

      <Modal open={isDeviceWarningOpen} className="student-modal-outer">
        <div className="student-modal-card">
          <button className="student-close-btn" onClick={goHome}>
            Close
          </button>

          <div className="student-login-heading">
            <p>DESKTOP REQUIRED</p>
            <h2>Open on laptop or desktop.</h2>
            <span>
              For the best learning experience, enrolled students should access
              the platform from a laptop or desktop. This helps with focused
              learning, coding practice, assignments and technical work.
            </span>
          </div>

          <button className="student-primary-btn" onClick={goHome}>
            Back to Home
          </button>
        </div>
      </Modal>
    </main>
  );
}